import React, { Fragment, ReactElement } from 'react';
import styled from '@emotion/styled';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { type } from '../../style';
import ButtonSet from '../../components/blocks/ButtonSet';
import Button from '../buttons/Button';
import { tsSince } from '../../common';
import { Message } from '@material-ui/icons';
import useMixPanel from '../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { useNavigate } from 'react-router-dom';
import { areEqual } from '../../utils/helpers';
import WYPButtonIcon from '../icons/WYPButtonIcon';
import breakpoints from 'theme/material/breakpoints';
import OfferAmountButton from 'atoms/buttons/OfferAmountButton';

const PriceContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const CustomAmountButtonStyle = {
  marginTop: 0,
  marginBottom: 0,
  [breakpoints.down(639.05)]: {
    width: 109,
    padding: 8,
    gap: 7,
    '.offer-amount-label': {
      fontSize: 32,
      lineHeight: '21px',
    },
  },
};

const Time = styled('div')({
  color: '#8492a6',
  textAlign: 'center',
  ...type.SM,
  marginTop: 10,
});

const ButtonDiv = styled('div')({
  [breakpoints.down(639.05)]: {
    width: '100%',
  },
});

const ButtonStyle = {
  backgroundColor: '#E21D4A',
  color: '#fff',
  diplay: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: '16px',
  letterSpacing: -0.28,
  padding: '10px 20px',
  borderRadius: '4px !important',
  '&:hover': {
    backgroundColor: '#E21D4A !important',
    color: '#fff !important',
    borderColor: '#E21D4A !important',
  },
  [breakpoints.down(639.05)]: {
    fontSize: 13,
    padding: '6px 16px',
    letterSpacing: -0.26,
  },
};

interface AcceptedOfferPanelProps {
  offer: any;
  createdAt: any;
  offerPrice: any;
  myProfile: any;
  otherProfile: any;
  otherAccount: any;
  isGenerous: any;
  unlockMessage: any;
}

const AcceptedOfferPanel = ({
  offer,
  otherAccount,
  myProfile,
  createdAt,
  isGenerous,
  unlockMessage,
}: AcceptedOfferPanelProps): ReactElement => {
  const { mixpanelData, resetMixpanelData, trackPackagesPageVisited } = useMixPanel();

  const navigate = useNavigate();

  const handleGoToConversation = () => {
    navigate(`/mail/${offer?.thread_uid ?? otherAccount.hash_id}`);
  };

  const handleUnlockConversation = () => {
    const creditCost = offer.credit_cost;
    const currentCredit = myProfile.credits;
    const hashId = otherAccount.hash_id;

    if (creditCost > currentCredit) {
      trackPackagesPageVisited({ Source: 'Unlock Attempt' });
      navigate('/packages');
      return;
    }

    unlockMessage(hashId, mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy, () => {
      resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
    });
  };

  const formatPrice = (currency, price) => {
    if (!currency || !price) {
      return '';
    }

    return `${currency.initial}${String.fromCharCode(currency.label)}${price}`;
  };

  return (
    <Fragment>
      <div>
        <PriceContainer>
          <OfferAmountButton
            type={'receive'}
            price={formatPrice(
              {
                initial: offer ? offer.currency_original.data.initial : '',
                label: offer ? offer.currency_original.data.label_unicode : '',
              },
              offer.price_original
            )}
            viewType={'grid'}
            hasHoverEffect={false}
            onClick={() => ''}
            customBoxStyle={CustomAmountButtonStyle}
          />
        </PriceContainer>
        {createdAt ? <Time>{createdAt && tsSince(createdAt)}</Time> : null}
      </div>
      <ButtonDiv>
        <ButtonSet align="center">
          {offer.is_deal_unlocked || !isGenerous ? (
            <Button
              id={'btn-go-to-conversation'}
              onClick={handleGoToConversation}
              type="button"
              buttonType="error"
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customStyle={ButtonStyle}
            >
              <Fragment>
                <Message color="inherit" />
                &ensp;
                <span>Go To Messages</span>
              </Fragment>
            </Button>
          ) : (
            <Button
              id={'btn-unlock-conversation'}
              onClick={handleUnlockConversation}
              type="button"
              buttonType="error"
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customStyle={ButtonStyle}
            >
              <WYPButtonIcon style={{ marginRight: 6 }} />
              <span>Unlock Messages</span>
            </Button>
          )}
        </ButtonSet>
      </ButtonDiv>
    </Fragment>
  );
};

export default React.memo(AcceptedOfferPanel, areEqual);
